import React, { useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import shortid from 'shortid';
import { __ } from 'locale';
import Util from 'utility';
import {
    openModal, closeModal,
    updateMeal, removeMeal, addFoodGroup,
} from '../../../actions/index';
import LectureTaskMealDescription from './LectureTaskMealDescription';
import ConfirmDialog from 'components/shared/ConfirmDialog';
import DropdownMenu from 'components/shared/DropdownMenu';
import LectureTaskPanelSave from './LectureTaskPanelSave';
import EvaluationPanelContents from 'components/EvaluationPanel/EvaluationPanelContents';
import evaluateTask from 'components/Plan/LectureTask/TaskEvaluator';
import FoodGroup from './FoodGroup';

const fmtEvalValue = (v, precision = 2) => (v * 1).toFixed(precision);

function MealItem({ mealHash: m, mealsAreEditable, activePanel, togglePanel, closePanel, foodGroupVisibility, onToggleFoodGroupVisibility, focusedAmountInputId, focusedAmountInputValue, onAmountFocus, onAmountChange, onAmountBlur, onSave, onSaveAndUpdate, onChange }) {
    const dispatch = useDispatch();
    const { id: paramId } = useParams();
    const editMealRef = useRef({ hash: m, mealId: 1, hour: 0, minute: 0 });

    const data = useSelector(state => state);
    const { globals, meals, foodGroups, mealsEvaluationResults, evaluationResults, appModeData } = data;

    const meal = meals[m];
    const mealId = meal.mealId;
    if (typeof globals.mealTypes[mealId] === 'undefined') return null;

    const doUpdateMeal = () => {
        const ref = editMealRef.current;
        dispatch(updateMeal({
            mealHash: ref.hash,
            mealId: ref.mealId,
            mealLength: 0,
            startHour: ref.hour,
            startMinute: ref.minute,
            profiles: meals[ref.hash]?.profiles || []
        }));
        onChange();
        dispatch(closeModal());
    };

    const handleEditMeal = () => {
        editMealRef.current = { hash: m, mealId: meal.mealId, hour: meal.startHour, minute: meal.startMinute };

        const hours = Array.from({ length: 25 }, (_, i) => i);
        const minutes = Array.from({ length: 60 }, (_, i) => i);

        let mealTypes = [];
        for (let i in globals.mealTypes) {
            mealTypes.push(<option value={i} key={i}>{globals.mealTypes[i].title}</option>);
        }

        let typeSelectElement = (
            <select name="select-meal-type" defaultValue={meal.mealId} onChange={e => { editMealRef.current.mealId = e.target.value; }}>
                {mealTypes}
            </select>
        );
        if (Util.isMealDrinkRegime(m)) {
            const x = m.split('|')[0] * 1;
            typeSelectElement = <p><b>{globals.mealTypes[x].title}</b></p>;
        }

        const selectCls = 'border border-slate-200 rounded-lg px-3 py-1.5 text-sm';
        dispatch(openModal({
            title: 'Upravit chod',
            content: (
                <div className="p-2 flex flex-col gap-3">
                    <div className="flex items-center gap-4">
                        <label className="w-24 text-sm text-slate-500 shrink-0">Typ chodu:</label>
                        {typeSelectElement}
                    </div>
                    <div className="flex items-center gap-4">
                        <label className="w-24 text-sm text-slate-500 shrink-0">Čas:</label>
                        <div className="flex items-center gap-2">
                            <select className={selectCls} name="select-time-start-h" defaultValue={meal.startHour} onChange={e => { editMealRef.current.hour = e.target.value; }}>
                                {hours.map(n => <option value={n} key={n}>{n}</option>)}
                            </select>
                            <span className="text-slate-400">:</span>
                            <select className={selectCls} name="select-time-start-m" defaultValue={meal.startMinute} onChange={e => { editMealRef.current.minute = e.target.value; }}>
                                {minutes.map(n => <option value={n} key={n}>{n}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="flex items-center gap-6 mt-1">
                        <button className="bg-green-500 text-white py-2 px-4 rounded-lg text-sm font-medium cursor-pointer border-none hover:bg-green-600 transition-colors" onClick={doUpdateMeal}>Uložit změny</button>
                        <a className="text-slate-400 underline cursor-pointer text-sm" onClick={() => dispatch(closeModal())}>Zpět</a>
                    </div>
                </div>
            )
        }));
    };

    const doDeleteMeal = () => {
        dispatch(removeMeal(m));
        onSaveAndUpdate();
    };

    const handleDeleteMeal = () => {
        dispatch(openModal({
            title: __('Vymazat chod'),
            content: <ConfirmDialog message={__('Opravdu vymazat chod?')} confirmLabel={__('Ano, vymazat chod')} onConfirm={doDeleteMeal} />
        }));
    };

    const openSaveToMyRecipes = (e) => {
        closePanel();
        e.preventDefault();
        if (e.target !== e.currentTarget) return;
        const currentRecipeId = typeof paramId !== 'undefined' ? paramId : 0;
        dispatch(closeModal());
        dispatch(openModal({
            title: __('Uložit recept'),
            class: 'dialog-big',
            content: <LectureTaskPanelSave mealHash={m} currentRecipeId={currentRecipeId} onSaved={() => dispatch(closeModal())} />
        }));
    };

    const openEditMealDescription = (e) => {
        closePanel();
        e.preventDefault();
        if (e.target !== e.currentTarget) return;
        dispatch(closeModal());
        dispatch(openModal({
            title: __('Postup přípravy receptu'),
            class: 'dialog-flexible',
            closeButton: __('Uložit a zavřít'),
            onClose: onSave,
            content: <LectureTaskMealDescription mealHash={m} foodGroupId={undefined} />
        }));
    };

    const openAddFoodGroup = () => {
        const g = shortid();
        dispatch(addFoodGroup({ mealHash: m, id: g, title: '', description: '', type: 'plate', opened: true }));
        onSaveAndUpdate();
    };

    const buildMealEvaluationContent = () => {
        let profileId = Util.getActiveProfileId();
        if (typeof appModeData.profileId === 'number') profileId = appModeData.profileId;

        const thisMealHash = m.split('|')[0] + '|' + profileId;

        const res = evaluateTask(data, { dddInfo: undefined }, {
            lectureInfo: appModeData.lectureInfoData,
            mode: 'meal',
            mealHash: thisMealHash,
            evaluationResults: mealsEvaluationResults[m],
        });

        let tasksList = [];
        const { taskListMeals, taskListMealsOk, taskListUndesirableFoodsUsed, taskListDDD, taskListDDDOk } = res;

        if (taskListMeals.length > 0 || taskListMealsOk.length > 0) {
            tasksList.push(<h3 className="text-xs m-0 text-slate-500 uppercase font-extrabold tracking-wider mb-1" key="h-meals">{__('Chody / chuť')}</h3>);
            tasksList.push(...taskListMeals);
            tasksList.push(...taskListMealsOk);
        }
        if (taskListUndesirableFoodsUsed.length > 0) {
            tasksList.push(...taskListUndesirableFoodsUsed);
        }
        if (taskListDDD.length > 0 || taskListDDDOk.length > 0) {
            tasksList.push(<h3 className="text-xs m-0 text-slate-500 uppercase font-extrabold tracking-wider mb-1 mt-2" key="h-ddd">{__('Nutriční hodnoty')}</h3>);
            tasksList.push(
                <div key="ddd">
                    {taskListDDD}
                    {taskListDDDOk}
                </div>
            );
        }

        const mealEvalResults = mealsEvaluationResults[m];
        let watchAttribs = [];

        if (typeof mealEvalResults !== 'undefined') {
            const thisMealIsEmpty = mealEvalResults.weight === 0 && mealEvalResults.energy_kcal === 0;
            const lectureWatchAttribs = appModeData.lectureInfoData.lecture_data.watch_attributes.split(',');
            let watchArray = ['gi', 'gn', 'digestibility', 'thermic_effect', 'acidobasicity', 'energy'];
            const mealWatchSettings = appModeData.lectureInfoData.watch_attributes_meal;
            if (typeof mealWatchSettings === 'object') {
                lectureWatchAttribs.forEach(w => { if (mealWatchSettings.includes(w)) watchArray.push(w); });
            }

            const attribRowCls = 'flex justify-between items-center py-1 text-xs border-b border-slate-100';
            watchArray.forEach(attr => {
                if (!lectureWatchAttribs.includes(attr)) return;

                if (attr === 'gi') {
                    const v = mealEvalResults[attr];
                    let text = v > 70 ? __('vysoký') : v > 50 ? __('střední') : __('nízký');
                    if (thisMealIsEmpty) text = '-';
                    watchAttribs.push(<div key={attr} className={attribRowCls}><p className="m-0">{globals.attributes[attr]}:</p><aside>{text}</aside></div>);
                } else if (attr === 'gn') {
                    const v = mealEvalResults[attr];
                    let text = v > 20 ? __('vysoká') : v > 10 ? __('střední') : __('nízká');
                    if (thisMealIsEmpty) text = '-';
                    watchAttribs.push(<div key={attr} className={attribRowCls}><p className="m-0">{globals.attributes[attr]}:</p><aside>{text}</aside></div>);
                } else if (attr.indexOf('_ratio') > 0) {
                    watchAttribs.push(<div key={attr} className={attribRowCls}><p className="m-0">{globals.attributes[attr]}:</p><aside>{mealEvalResults[attr]}</aside></div>);
                } else {
                    const attr2 = attr + '_n';
                    const val = +mealEvalResults[attr2];
                    const textMap = {
                        acidobasicity_n: globals.evaluationTexts['acidobasicity'][val],
                        digestibility_n: globals.evaluationTexts['digestibility'][val],
                        energy_n: globals.evaluationTexts['energytendency'][val],
                        thermic_effect_n: globals.evaluationTexts['thermiceffect'][val],
                    };
                    let text = textMap[attr2] || '';
                    if (thisMealIsEmpty) text = '-';
                    watchAttribs.push(<div key={attr} className={attribRowCls}><p className="m-0">{globals.attributes[attr]}:</p><aside>{text}{'\u00A0'}{globals.attribute_units[attr]}</aside></div>);
                }
            });
        }

        const watchAttribsEl = watchAttribs.length > 0
            ? <div className="px-4"><div className="mb-3"><h3 className="text-xs m-0 text-slate-500 uppercase font-extrabold tracking-wider mb-1">Sledujte</h3>{watchAttribs}</div></div>
            : false;

        const beforeContent = tasksList.length > 0
            ? <><div className="px-4 py-3 text-xs"><div className="mb-3">{tasksList}</div></div>{watchAttribsEl}</>
            : <>{watchAttribsEl}</>;

        return (
            <EvaluationPanelContents
                standalone={true}
                beforeContent={beforeContent}
                wholeDay={false}
                results={mealsEvaluationResults[m]}
                ddd={evaluationResults.ddd}
                quickResults={false}
                evaluationCollapsedAtStart={false}
                macrosOnly={true}
            />
        );
    };

    const showMealEvaluation = () => {
        const title = Util.getLang() === 'sk' ? 'Nutričné vyhodnotenie chodu' : 'Nutriční vyhodnocení chodu';
        dispatch(closeModal());
        dispatch(openModal({ title, content: buildMealEvaluationContent() }));
    };

    let sh = meal.startHour < 10 ? '0' + meal.startHour : meal.startHour;
    let sm = meal.startMinute < 10 ? '0' + meal.startMinute : meal.startMinute;

    const thisItemMealId = m.split('|')[0];
    const isDrinkRegime = globals.mealTypes[thisItemMealId].is_drink_regime;
    const thisMealIsDrinkRegime = globals.mealTypes[mealId].is_drink_regime;

    const mealItemCls = `w-[calc(100%-76px)] my-4 ml-[70px] max-[959px]:my-3 max-[959px]:ml-10 rounded-xl border border-slate-200 border-l-4 bg-white shadow-sm cursor-default text-sm ${isDrinkRegime ? 'border-l-sky-400' : 'border-l-green-500'}`;

    const mealSettingsPanel = activePanel.type === 'mealSettings' && activePanel.key === m
        ? (
            <DropdownMenu
                top="2.25em"
                onClose={closePanel}
                items={[
                    { label: __('Uložit do mých receptů'), onClick: openSaveToMyRecipes },
                    { label: __('Popis přípravy'), onClick: openEditMealDescription },
                    Util.isMealDrinkRegime(m) && { label: 'Upravit', onClick: handleEditMeal },
                    (mealsAreEditable || thisMealIsDrinkRegime) && { label: 'Smazat', onClick: handleDeleteMeal, danger: true },
                ]}
            />
        ) : false;

    const sortedFoodGroupKeys = Object.keys(foodGroups[m])
        .map(g => ({ id: g, item_order: foodGroups[m][g].item_order }))
        .sort((a, b) => a.item_order - b.item_order);

    const ev = typeof mealsEvaluationResults[m] === 'object' ? mealsEvaluationResults[m] : false;
    const macroLabels = [['Bílkoviny', ev && ev.proteins], ['Tuky', ev && ev.fats], ['Sacharidy', ev && ev.carbohydrates], ['Vláknina', ev && ev.fiber]];

    return (
        <div className={mealItemCls}>
            <div className="flex flex-wrap items-center py-3 px-4 border-b border-slate-100 text-slate-700">
                <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4 text-slate-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    </svg>
                    <span className="font-mono text-xs text-slate-500">{sh}:{sm}</span>
                    <b className="text-sm font-semibold text-slate-800 uppercase tracking-wide">{globals.mealTypes[mealId].title}</b>
                </div>
                <aside className="ml-auto flex items-center gap-2">
                    <a className="inline-flex items-center gap-1.5 text-xs text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-full px-3 py-1.5 transition-colors cursor-pointer" onClick={showMealEvaluation}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-3.5 h-3.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6a7.5 7.5 0 1 0 7.5 7.5h-7.5V6Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0 0 13.5 3v7.5Z" />
                        </svg>
                        {__('Vyhodnocení')}
                    </a>
                    <div className="relative">
                        <a className="click-outside-disabled inline-flex items-center justify-center w-7 h-7 text-slate-400 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors cursor-pointer" onClick={() => togglePanel('mealSettings', m)}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
                            </svg>
                        </a>
                        {mealSettingsPanel}
                    </div>
                </aside>
                {ev && (
                    <div className="basis-full flex pt-2 gap-6 border-t border-slate-50 mt-2">
                        {macroLabels.map(([label, value]) => (
                            <div key={label} className="text-xs text-slate-400">
                                {__(label)}: <b className="text-slate-600">{fmtEvalValue(value, 1)} g</b>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {sortedFoodGroupKeys.map(k => (
                <FoodGroup
                    key={k.id}
                    foodGroup={foodGroups[m][k.id]}
                    mealHash={m}
                    activePanel={activePanel}
                    togglePanel={togglePanel}
                    closePanel={closePanel}
                    foodGroupVisibility={foodGroupVisibility}
                    onToggleVisibility={onToggleFoodGroupVisibility}
                    focusedAmountInputId={focusedAmountInputId}
                    focusedAmountInputValue={focusedAmountInputValue}
                    onAmountFocus={onAmountFocus}
                    onAmountChange={onAmountChange}
                    onAmountBlur={onAmountBlur}
                    onSave={onSave}
                    onSaveAndUpdate={onSaveAndUpdate}
                />
            ))}
            {!thisMealIsDrinkRegime && (
                <div className="py-3 px-4 mt-1">
                    <a className="group inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 cursor-pointer transition-colors" onClick={openAddFoodGroup}>
                        <span className="flex items-center justify-center w-6 h-6 bg-green-500 text-white rounded-full group-hover:bg-green-600 transition-colors flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor" className="w-3 h-3">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                        </span>
                        {__('Přidat talíř')}
                    </a>
                </div>
            )}
        </div>
    );
}

export default MealItem;
